/**
 * IOT-COMPLIANCE-V0-1 — FSMA 204 schemas.
 *
 * Authority: FSMA 204 §1.1455 (Key Data Elements / Critical Tracking Events)
 * Companion service: src/services/iot/compliance/index.js
 * Companion CF:      functions/src/iot/compliance/generateFsma204Pdf.js
 *
 * Contract:
 *   • Append-only — every regeneration creates a NEW version row.
 *   • Idempotent — same shipment + same data version = cached artifact.
 *   • Tenant-scoped — every record carries tenantId.
 *   • Signed — pdfStorageRef + archimedesArtifactRef bind the version.
 */
import { z } from 'zod';
import { TenantIdSchema, UlidSchema, IsoDateSchema } from './_primitives.js';

// ── Excursion severity (matches IoT alert severity vocabulary) ──────────────
export const ExcursionSeverity = z.enum(['advisory', 'warning', 'critical']);

// ── Door-correlation flag — set when an excursion overlaps a door-open event
export const DoorCorrelation = z.object({
  occurred:     z.boolean(),
  doorOpenAt:   IsoDateSchema.nullable().optional(),
  doorCloseAt:  IsoDateSchema.nullable().optional(),
});

// ── Single temperature reading (or other sensor reading) point ──────────────
export const TempReadingPoint = z.object({
  id:            z.string(),
  observedAt:    IsoDateSchema,
  tempC:         z.number(),
  setpointC:     z.number().nullable().optional(),
  ambientC:      z.number().nullable().optional(),
  humidityPct:   z.number().nullable().optional(),
  shockG:        z.number().nullable().optional(),
  doorState:     z.enum(['open', 'closed', 'unknown']).optional(),
  gpsLat:        z.number().nullable().optional(),
  gpsLng:        z.number().nullable().optional(),
  gpsAltM:       z.number().nullable().optional(),
});

// ── Excursion event — derived (not raw) ─────────────────────────────────────
export const ExcursionEvent = z.object({
  id:                  z.string(),
  startedAt:           IsoDateSchema,
  endedAt:             IsoDateSchema.nullable(),
  durationMs:          z.number().nonnegative(),
  thresholdProfileId:  z.string(),
  thresholdMinC:       z.number().nullable().optional(),
  thresholdMaxC:       z.number().nullable().optional(),
  peakTempC:           z.number(),
  severity:            ExcursionSeverity,
  doorCorrelation:     DoorCorrelation.nullable().optional(),
  gpsLat:              z.number().nullable().optional(),
  gpsLng:              z.number().nullable().optional(),
  // AEGIS-synthesized root-cause analysis reference
  aegisRootCauseRef:   z.string().nullable().optional(),
});

// ── KDE/CTE block per FSMA 204 §1.1455 ──────────────────────────────────────
// Each block describes one critical tracking event in the chain of custody.
export const FsmaKdeBlock = z.object({
  cteType:           z.enum(['receiving', 'shipping', 'transformation', 'first-receiver', 'creation']),
  cteOccurredAt:     IsoDateSchema,
  // Key data elements (KDE) — partial coverage; full set per §1.1455
  ftlFood:           z.string().describe('FTL food name / commodity'),
  lotCode:           z.string(),
  quantity:          z.number().nonnegative(),
  unit:              z.string(),                    // e.g., 'lb', 'kg', 'case'
  immediatePreviousSourceLocation: z.string().nullable().optional(),
  immediateSubsequentRecipientLocation: z.string().nullable().optional(),
  shipFromLocation:  z.string().nullable().optional(),
  shipToLocation:    z.string().nullable().optional(),
  referenceDocumentNumber: z.string().nullable().optional(),
});

// ── Signature block — operator-driven + customer-driven (KAUDIT-8 R6) ───────
export const FsmaSignatureBlock = z.object({
  operatorSignedAt:   IsoDateSchema.nullable(),
  operatorUserId:     z.string().nullable(),
  customerSignedAt:   IsoDateSchema.nullable(),
  customerUserId:     z.string().nullable(),
});

// ── Full FSMA 204 document (one version row per generation) ─────────────────
export const FSMA204Document = z.object({
  id:                  UlidSchema,
  tenantId:            TenantIdSchema,
  shipmentId:          z.string(),
  version:             z.string().describe('data-version hash; same version = same content'),

  generatedAt:         IsoDateSchema,
  generatedByUserId:   z.string().nullable(),

  kdeBlocks:           z.array(FsmaKdeBlock),
  temperatureReadings: z.array(TempReadingPoint),
  excursions:          z.array(ExcursionEvent),

  signatureBlock:      FsmaSignatureBlock,

  pdfStorageRef:       z.string().describe('gs:// path to operator-internal PDF'),
  pdfCustomerShareRef: z.string().nullable().optional().describe('gs:// path to tenant-branded customer-share PDF'),
  archimedesArtifactRef: z.string().describe('artifact ref returned by archimedes/registerArtifact'),

  correlationId:       z.string(),
  softDeleted:         z.boolean(),
});

// ── ExcursionRootCauseAnalysis — AEGIS schema for synthesizeRootCause() ─────
export const ExcursionRootCauseAnalysis = z.object({
  excursionId:        z.string(),
  rootCauseHypothesis: z.string(),
  contributingFactors: z.array(z.string()),
  evidenceCitations:   z.array(z.object({
    kind:        z.enum(['reading', 'door-event', 'gps-event', 'shipment-event']),
    referenceId: z.string(),
    snippet:     z.string(),
  })),
  confidence:          z.number().min(0).max(1),
  generatedAt:         IsoDateSchema,
  modelVersion:        z.string(),
});
